import type { Branch } from "@domain/CRM/branch";
import type { Deal } from "@domain/CRM/deal";
import type { AgrianTypes, AgrianEventSelections } from "@domain/Integrations/Agrian";
import { pickDefaultEventsForFields } from "@domain/Integrations/Agrian";
import { Job } from "@domain/CRM/job";
import { normalizePointsFC } from "@domain/Geo/sample";
import type { User } from "@domain/Auth";
import type { AgrianType, IAgrianApi } from "@ports/integrations/IAgrianApi";
import type { IAgrianGeoFetcher } from "@ports/integrations/IAgrianGeoFetcher";

export interface AgrianUseCasesDeps {
	agrianApi: IAgrianApi;
	geoFetcher: IAgrianGeoFetcher;
}

export interface LoadOrganizationsInput {
	user: User | undefined;
	agrianType: AgrianType;
	/** Org IDs derived from the active company + selected branches. Used as
	 *  the by-ids fallback when the user is not an admin (i.e. they're
	 *  scoped to a specific subset of orgs). */
	currentOrgIds: string[];
	forceRefresh?: boolean;
}

export interface FarmsAndFieldsResult {
	farms: AgrianTypes.Farm[];
	fields: AgrianTypes.Field[];
	/** Pre-computed (n−1) deselection subsets for the caller's grower-set
	 *  cache: keyed by sorted comma-joined grower-ids. The vm seeds these
	 *  into its own cache so deselecting a grower is a hit, not a refetch. */
	subsets: Map<string, { farms: AgrianTypes.Farm[]; fields: AgrianTypes.Field[] }>;
}

export interface PrefetchEventsInput {
	fieldIds: string[];
	fields: AgrianTypes.Field[];
	agrianType: AgrianType;
	isZoneDeal: boolean;
	/** Optional progress reporter; the GUI threads this into a wizard
	 *  progress bar. CLI/tests can omit it. */
	onProgress?: (progress: { current: number; total: number; label?: string }) => void;
}

export interface PrefetchEventsResult {
	targetEvents: AgrianTypes.HydratedTargetEvent[];
	sampleEvents: AgrianTypes.HydratedSampleEvent[];
	zones: AgrianTypes.HydratedZone[];
	selections: AgrianEventSelections;
	hasAmbiguity: boolean;
}

export interface BuildJobsInput {
	selectedFields: AgrianTypes.Field[];
	farms: AgrianTypes.Farm[];
	selectedGrowers: AgrianTypes.Grower[];
	prefetched: {
		targetEvents: AgrianTypes.HydratedTargetEvent[];
		sampleEvents: AgrianTypes.HydratedSampleEvent[];
		zones: AgrianTypes.HydratedZone[];
	};
	selections: AgrianEventSelections;
	deal: Deal | null;
	branch: Branch | null;
	user: User | undefined;
	isZoneDeal: boolean;
	/** Per-field cache lookup; when supplied AND it returns a Job, that
	 *  field is reused verbatim (no geo-fetcher calls). */
	getFromCache?: (fieldId: string) => Job | undefined;
	/** Per-field cache write; called whenever a fresh Job is built. */
	setInCache?: (fieldId: string, job: Job) => void;
}

export interface BuildJobsResult {
	jobs: Job[];
	featureStats: { zones: number; centroids: number; cores: number };
}

/**
 * Drives the Agrian-import workflow IO. The vm keeps its UI state, the
 * three Map-keyed caches (organizations / growers / farms-fields by
 * grower-set / per-field jobs), the React refs that buildJobs reads
 * synchronously after prefetch, and the auto-load cascade — but every
 * fetch+dedup+derivation step lives here.
 */
export interface AgrianUseCases {
	loadOrganizations(input: LoadOrganizationsInput): Promise<AgrianTypes.Organization[]>;
	loadGrowers(orgIds: string[], agrianType: AgrianType, forceRefresh?: boolean): Promise<AgrianTypes.Grower[]>;
	loadFarmsAndFields(
		growerIds: string[],
		agrianType: AgrianType,
		forceRefresh?: boolean,
	): Promise<FarmsAndFieldsResult>;
	prefetchEventsForFields(input: PrefetchEventsInput): Promise<PrefetchEventsResult>;
	buildJobsFromSelections(input: BuildJobsInput): Promise<BuildJobsResult>;
}

export async function loadOrganizations(
	deps: AgrianUseCasesDeps,
	input: LoadOrganizationsInput,
): Promise<AgrianTypes.Organization[]> {
	let loaded: AgrianTypes.Organization[] = [];
	if (input.user?.isAdmin) {
		loaded = await deps.agrianApi.getOrganizations(input.agrianType, input.forceRefresh);
	} else if (input.currentOrgIds.length > 1) {
		loaded = await deps.agrianApi.getOrganizationsByIds(input.currentOrgIds, input.agrianType, input.forceRefresh);
	}
	return Array.from(new Map(loaded.map((o) => [o.id, o])).values());
}

export async function loadGrowers(
	deps: AgrianUseCasesDeps,
	orgIds: string[],
	agrianType: AgrianType,
	forceRefresh?: boolean,
): Promise<AgrianTypes.Grower[]> {
	const loaded = await deps.agrianApi.getGrowers(orgIds, agrianType, forceRefresh);
	return Array.from(new Map(loaded.map((g) => [g.id, g])).values());
}

export async function loadFarmsAndFields(
	deps: AgrianUseCasesDeps,
	growerIds: string[],
	agrianType: AgrianType,
	forceRefresh?: boolean,
): Promise<FarmsAndFieldsResult> {
	const loadedFarms = await deps.agrianApi.getFarms(growerIds, agrianType, forceRefresh);
	const farms = Array.from(new Map(loadedFarms.map((f) => [f.id, f])).values());
	const farmIds = farms.map((f) => f.id);
	const loadedFields = await deps.agrianApi.getFields(farmIds, agrianType, forceRefresh);
	const fields = Array.from(new Map(loadedFields.map((f) => [f.id, f])).values());

	const subsets = new Map<string, { farms: AgrianTypes.Farm[]; fields: AgrianTypes.Field[] }>();
	for (let i = 0; i < growerIds.length; i++) {
		const subsetIds = growerIds.filter((_, j) => j !== i);
		if (subsetIds.length > 0) {
			const subKey = [...subsetIds].sort().join(",");
			if (!subsets.has(subKey)) {
				const subFarms = farms.filter((f) => subsetIds.includes(f.grower_id));
				const subFarmIds = new Set(subFarms.map((f) => f.id));
				const subFields = fields.filter((f) => subFarmIds.has(f.farm_id));
				subsets.set(subKey, { farms: subFarms, fields: subFields });
			}
		}
	}

	return { farms, fields, subsets };
}

export async function prefetchEventsForFields(
	deps: AgrianUseCasesDeps,
	input: PrefetchEventsInput,
): Promise<PrefetchEventsResult> {
	const empty: PrefetchEventsResult = {
		targetEvents: [],
		sampleEvents: [],
		zones: [],
		selections: {},
		hasAmbiguity: false,
	};
	if (input.fieldIds.length === 0) return empty;

	const totalSteps = input.fieldIds.length + 1 + (input.isZoneDeal ? 1 : 0);
	let loaded = 0;
	input.onProgress?.({ current: 0, total: totalSteps, label: "Loading sampling events…" });

	input.onProgress?.({ current: loaded, total: totalSteps, label: "Loading target events…" });
	const targetEvents = await deps.agrianApi.getTargetEvents(input.fieldIds, input.agrianType);
	loaded++;

	let zones: AgrianTypes.HydratedZone[] = [];
	if (input.isZoneDeal) {
		input.onProgress?.({ current: loaded, total: totalSteps, label: "Loading zones…" });
		zones = await deps.agrianApi.getZones(input.fieldIds, input.agrianType);
		loaded++;
	}

	const sampleEvents: AgrianTypes.HydratedSampleEvent[] = [];
	for (const field of input.fields) {
		input.onProgress?.({ current: loaded, total: totalSteps, label: `Sampling events — ${field.name}` });
		const fieldSamples = await deps.agrianApi.getSampleEvents([field.id], input.agrianType);
		sampleEvents.push(...fieldSamples);
		loaded++;
	}

	const { selections, hasAmbiguity } = pickDefaultEventsForFields(input.fields, sampleEvents, targetEvents, zones);

	return { targetEvents, sampleEvents, zones, selections, hasAmbiguity };
}

export async function buildJobsFromSelections(
	deps: AgrianUseCasesDeps,
	input: BuildJobsInput,
): Promise<BuildJobsResult> {
	const { targetEvents, sampleEvents, zones } = input.prefetched;
	const newJobs: Job[] = [];

	for (const field of input.selectedFields) {
		const cached = input.getFromCache?.(field.id);
		if (cached) {
			newJobs.push(cached);
			continue;
		}
		const farm = input.farms.find((f) => f.id === field.farm_id);
		if (!farm) continue;
		const grower = input.selectedGrowers.find((g) => g.id === farm.grower_id);
		if (!grower) continue;

		const boundaryGeoJSON = deps.geoFetcher.getBoundaryGeoJSON([field.boundary_map]);

		const choice = input.selections[field.id];
		let pointsGeoJSON: import("geojson").FeatureCollection | undefined;
		let zonesGeoJSON: import("geojson").FeatureCollection | undefined;

		if (choice) {
			if (choice.source === "sample") {
				const event = sampleEvents.find((e) => e.id === choice.eventId);
				if (event) {
					const centroids = deps.geoFetcher.getSampleEventCentroidsGeoJSON(event);
					const cores = deps.geoFetcher.getSampleEventCoresGeoJSON(event);
					const zns = deps.geoFetcher.getSampleEventZonesGeoJSON(event);
					pointsGeoJSON = input.isZoneDeal ? (cores ?? undefined) : (centroids ?? undefined);
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			} else if (choice.source === "target") {
				const event = targetEvents.find((e) => e.id === choice.eventId);
				if (event) {
					const centroids = deps.geoFetcher.getTargetEventCentroidsGeoJSON(event);
					const zns = deps.geoFetcher.getTargetEventZonesGeoJSON(event);
					if (!input.isZoneDeal) pointsGeoJSON = centroids ?? undefined;
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			} else if (choice.source === "zone-map") {
				const event = zones.find((e) => e.id === choice.eventId);
				if (event) {
					const zns = deps.geoFetcher.getZoneMapGeoJSON(event);
					if (input.isZoneDeal) zonesGeoJSON = zns ?? undefined;
				}
			}
		}

		const newJob: Job = Job.defaultForImport({
			fieldName: field.name,
			depth: input.deal?.depth,
			density: input.deal?.density,
			growerName: grower.name,
			frequencyOfMicros: input.deal?.frequencyOfMicros,
			testPackage: input.deal?.testPackage,
			farmName: farm.name,
		});

		newJob.files = {
			pointsGeoJSON: pointsGeoJSON ? normalizePointsFC(pointsGeoJSON) : undefined,
			boundaryGeoJSON,
			zonesGeoJSON,
		};
		newJob.dealIds = input.deal?.id ? [input.deal.id] : [];
		newJob.externalFarmId = farm.id;
		newJob.fieldId = field.id;
		newJob.fieldPriority = "Regular Turn (default)";
		newJob.submitterEmail = input.user?.email;
		newJob.submitterName = input.user?.name;
		newJob.branchIds = input.branch ? [input.branch.id] : [];
		newJob.externalGrower = { id: grower.id, name: grower.name };
		newJob.eventId = "";

		input.setInCache?.(field.id, newJob);
		newJobs.push(newJob);
	}

	const featureStats = {
		zones: newJobs.reduce((sum, j) => sum + (j.files.zonesGeoJSON?.features?.length || 0), 0),
		centroids: newJobs.reduce(
			(sum, j) => sum + (input.isZoneDeal ? 0 : j.files.pointsGeoJSON?.features?.length || 0),
			0,
		),
		cores: newJobs.reduce((sum, j) => sum + (input.isZoneDeal ? j.files.pointsGeoJSON?.features?.length || 0 : 0), 0),
	};

	return { jobs: newJobs, featureStats };
}

export function makeAgrianUseCases(deps: AgrianUseCasesDeps): AgrianUseCases {
	return {
		loadOrganizations: (input) => loadOrganizations(deps, input),
		loadGrowers: (orgIds, agrianType, forceRefresh) => loadGrowers(deps, orgIds, agrianType, forceRefresh),
		loadFarmsAndFields: (growerIds, agrianType, forceRefresh) =>
			loadFarmsAndFields(deps, growerIds, agrianType, forceRefresh),
		prefetchEventsForFields: (input) => prefetchEventsForFields(deps, input),
		buildJobsFromSelections: (input) => buildJobsFromSelections(deps, input),
	};
}
